import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Form, Input, InputRef, Popover, Space } from "antd";
import { useForm } from "antd/es/form/Form";
import classNames from "classnames";
import { I18N_NS } from "Config/i18n";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Editor, Element, Transforms, Range } from "slate";
import { ReactEditor, useFocused, useSlate } from "slate-react";
import { LinkElement } from "../slate-types";

const isLinkActive = (editor) => {
  const [link] = Array.from(
    Editor.nodes(editor, {
      match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === "link",
    })
  );
  return !!link;
};
const wrapLink = (editor: Editor, { text, url, target }: { text: string; url: string; target?: "_blank" }) => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);
  const link: LinkElement = {
    type: "link",
    url: url,
    target: target,
    children: isCollapsed ? [{ text: text }] : [],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: "end" });
  }
  ReactEditor.focus(editor);
};

const unwrapLink = (editor: Editor) => {
  Transforms.unwrapNodes(editor, {
    match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === "link",
  });
};

export const LinkButtons = () => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const editor = useSlate();
  const [isOpen, setIsOpen] = useState(false);
  const [form] = useForm();
  const textInput = useRef<InputRef>();
  const targetInput = useRef<InputRef>();
  const editorFocused = useFocused();

  const handleOpenChange = (visible) => {
    if (!visible) {
      form.resetFields();
      return;
    }
    if (editor.selection) {
      const [linkElement] = Array.from(
        Editor.nodes(editor, {
          match: (n) => Element.isElement(n) && n.type === "link",
          at: editor.selection,
        })
      );
      if (linkElement) {
        const url = linkElement[0].url;
        form.setFieldValue("url", url);
      }
      const text = Editor.string(editor, editor.selection);
      if (text?.length) {
        form.setFieldValue("text", text);
        targetInput.current?.focus();
      } else {
        textInput.current?.focus();
      }
    } else {
      textInput.current?.focus();
    }
  };

  useEffect(() => {
    if (editorFocused) {
      setIsOpen(false);
    }
  }, [editorFocused]);

  const handleSubmit = ({ text, url }) => {
    setIsOpen(false);
    wrapLink(editor, { text, url, target: "_blank" });
  };
  const linkActive = isLinkActive(editor);
  return (
    <>
      <Popover
        open={isOpen}
        afterOpenChange={handleOpenChange}
        destroyOnHidden
        trigger={"click"}
        content={
          <Form onFinish={handleSubmit} size="small" form={form}>
            <Form.Item name="text" rules={[{ required: true, message: t("This field is required") }]}>
              <Input placeholder={t("Link text")} ref={textInput} />
            </Form.Item>
            <Form.Item name="url" rules={[{ required: true, message: t("This field is required") }]}>
              <Input placeholder={t("Link target")} ref={targetInput} />
            </Form.Item>
            <Space>
              <Button onClick={() => setIsOpen(false)}>{t("Cancel")}</Button>
              <Button type="primary" htmlType="submit">
                {t("Add")}
              </Button>
            </Space>
          </Form>
        }
      >
        <div
          title={t(`${I18N_NS.RICH_EDITOR}:::Link`)}
          className={classNames(["slate--mark-btn", { ["mark-active"]: linkActive }, { ["mark-disabled"]: false }])}
          onMouseDown={(event) => {
            event.preventDefault();
            setIsOpen(!isOpen);
          }}
        >
          <FontAwesomeIcon icon={["far", "link"]} fixedWidth />
        </div>
      </Popover>
      <div
        title={t(`${I18N_NS.RICH_EDITOR}:::Unlink`)}
        className={classNames(["slate--mark-btn", { ["mark-active"]: false }, { ["mark-disabled"]: !linkActive }])}
        onMouseDown={(event) => {
          event.preventDefault();
          if (linkActive) {
            unwrapLink(editor);
          }
        }}
      >
        <FontAwesomeIcon icon={["far", "unlink"]} fixedWidth />
      </div>
    </>
  );
};
